/* customFieldEnumValidator.js */

/**
 * @module customFieldEnumValidator
 *
 * Post-Ajv validator for the JSON edit pane. Walks the if/then/else
 * expression trees in each rule and flags, for any `customFieldValues.<key>`
 * reference:
 *   - a `<key>` that resolves to no known custom field (by UUID or name), and
 *   - an enum-typed field given a value outside its allowed option labels.
 *
 * Encoded directly in Ajv this would require an `allOf:[{if,then}]` rule
 * per custom field — combinatorially messy. A focused tree walk is
 * clearer and produces sharper error messages.
 *
 * Returns a function with the signature `(parsedValue, text) → errors[]`
 * matching the shape produced by json_schema_validator so the gutter
 * marker pipeline doesn't need any special handling.
 */
const _ = require("lodash");
const {SchemaValidator} = require("@gluenetworks/json-editor");
const {walkMongoLeaves, walkQueryBuilderLeaves} = require("./expressionWalkers");

// `.*` (not `.+`) so a prefix with an empty key — "customFieldValues." — still
// matches and is flagged as unrecognized rather than slipping past both this
// validator and expressionFieldValidator (which defers all customFieldValues.* ).
const CUSTOM_FIELD_PATH_PATTERN = /^customFieldValues\.(.*)$/;
const ENUM_DATA_TYPES = ["Enum", "Dropdown"];

const _isMongoExpression = (expression) =>
    _.isObject(expression) &&
    !_.isArray(expression) &&
    (_.has(expression, "$and") || _.has(expression, "$or"));

const _resolveCustomField = (key, customFieldsByUuid, customFieldsByName) => {
    if (!key) {
        return undefined;
    }
    if (customFieldsByUuid[key]) {
        return customFieldsByUuid[key];
    }
    if (customFieldsByName[key]) {
        return customFieldsByName[key];
    }
    return undefined;
};

const _allowedLabelsFor = (customField) => {
    if (!customField || !ENUM_DATA_TYPES.includes(customField.dataType)) {
        return undefined;
    }
    const labels =
        customField.typeInfo &&
        _.isArray(customField.typeInfo.labels) &&
        customField.typeInfo.labels;
    if (!labels || !labels.length) {
        return undefined;
    }
    return labels;
};

const _checkValueAgainstLabels = (value, allowedLabels) => {
    if (_.isArray(value)) {
        return value.filter((v) => typeof v === "string" && !allowedLabels.includes(v));
    }
    if (typeof value === "string" && !allowedLabels.includes(value)) {
        return [value];
    }
    return [];
};

const _formatError = (text, pointer, fieldName, badValue, allowedLabels) => {
    const {line, column} = SchemaValidator.pointerToLineColumn(text, pointer);
    return {
        line,
        column,
        message: `[customField] "${badValue}" is not a valid value for ${fieldName} (allowed: ${allowedLabels.join(
            ","
        )})`,
        jsonPointer: pointer,
    };
};

const _formatUnknownFieldError = (text, pointer, fieldKey) => {
    const {line, column} = SchemaValidator.pointerToLineColumn(text, pointer);
    return {
        line,
        column,
        message: `[customField] Unrecognized custom field "${fieldKey}"`,
        jsonPointer: pointer,
    };
};

// Resolve a customFieldValues.<key> path to its enum option labels, or push an
// "unrecognized field" error. Returns the allowed-labels array, or null when
// there's nothing to check (non-custom path, or known non-enum field).
const _resolveEnumLabels = (fieldName, errorPointer, ctx) => {
    const match = CUSTOM_FIELD_PATH_PATTERN.exec(fieldName);
    if (!match) {
        return null;
    }
    const customField = _resolveCustomField(
        match[1],
        ctx.customFieldsByUuid,
        ctx.customFieldsByName
    );
    if (!customField) {
        ctx.errors.push(_formatUnknownFieldError(ctx.text, errorPointer, match[1]));
        return null;
    }
    return _allowedLabelsFor(customField) || null;
};

const _pushBadValues = (value, allowedLabels, fieldName, pointer, ctx) => {
    _checkValueAgainstLabels(value, allowedLabels).forEach((badValue) =>
        ctx.errors.push(_formatError(ctx.text, pointer, fieldName, badValue, allowedLabels))
    );
};

const _checkMongoLeaf = (fieldName, operatorObject, leafPointer, ctx) => {
    const allowedLabels = _resolveEnumLabels(fieldName, `${leafPointer}/${fieldName}`, ctx);
    if (!allowedLabels) {
        return;
    }
    if (_.isObject(operatorObject) && !_.isArray(operatorObject)) {
        _.each(operatorObject, (operatorValue, operatorName) =>
            _pushBadValues(
                operatorValue,
                allowedLabels,
                fieldName,
                `${leafPointer}/${fieldName}/${operatorName}`,
                ctx
            )
        );
    } else {
        _pushBadValues(
            operatorObject,
            allowedLabels,
            fieldName,
            `${leafPointer}/${fieldName}`,
            ctx
        );
    }
};

const _checkQueryBuilderLeaf = (leaf, leafPointer, ctx) => {
    if (!leaf || !leaf.id) {
        return;
    }
    const allowedLabels = _resolveEnumLabels(leaf.id, `${leafPointer}/id`, ctx);
    if (allowedLabels) {
        _pushBadValues(leaf.value, allowedLabels, leaf.id, `${leafPointer}/value`, ctx);
    }
};

const _walkExpressionsOnRule = (rule, basePointer, ctx) => {
    if (!_.isObject(rule)) {
        return;
    }
    if (rule.ifExpression && _isMongoExpression(rule.ifExpression)) {
        walkMongoLeaves(rule.ifExpression, `${basePointer}/ifExpression`, (field, ops, ptr) =>
            _checkMongoLeaf(field, ops, ptr, ctx)
        );
    }
    if (rule.thenExpression) {
        walkQueryBuilderLeaves(rule.thenExpression, `${basePointer}/thenExpression`, (leaf, ptr) =>
            _checkQueryBuilderLeaf(leaf, ptr, ctx)
        );
    }
    if (rule.elseExpression) {
        walkQueryBuilderLeaves(rule.elseExpression, `${basePointer}/elseExpression`, (leaf, ptr) =>
            _checkQueryBuilderLeaf(leaf, ptr, ctx)
        );
    }
};

/**
 * Build a validator function bound to the current labelMaps.
 *
 * Returned signature: `(parsedValue, text) → Array<{line, column, message, jsonPointer}>`
 *
 * Accepts both the policy shape (`{rules:[...]}`) and a single-rule shape
 * (e.g. the rule-edit subview's JSON pane) — it inspects which one and
 * walks accordingly.
 */
const buildCustomFieldEnumValidator = (labelMaps = {}) => {
    const customFieldsByUuid = labelMaps.customFields || {};
    const customFieldsByName = _.keyBy(
        _.values(customFieldsByUuid),
        (entry) => entry && entry.name
    );

    return (parsedValue, text) => {
        if (!_.isObject(parsedValue)) {
            return [];
        }
        const ctx = {
            errors: [],
            text: text || "",
            customFieldsByUuid,
            customFieldsByName,
        };

        if (_.isArray(parsedValue.rules)) {
            // Policy shape.
            parsedValue.rules.forEach((rule, idx) =>
                _walkExpressionsOnRule(rule, `/rules/${idx}`, ctx)
            );
        } else {
            // Single-rule shape (rule-edit subview).
            _walkExpressionsOnRule(parsedValue, "", ctx);
        }
        return ctx.errors;
    };
};

module.exports = {buildCustomFieldEnumValidator};
